/**
 * Hook: useFilterAutoSearch
 * SpecReferenceID: SEARCH-SEMANTIC-V1
 * Requirements: SEARCH-SEMANTIC-V1-R08
 *
 * Shared across the Semantic, Simple, and Galen search pages. Re-runs the search
 * (server-side filtering) whenever the user changes a refine selection, once an
 * initial search has been performed.
 *
 * Bug #5052: the previous per-page effect used a didMountRef guard that swallowed
 * the FIRST filter change after a search. That guard is removed here - the
 * `hasSearched` gate already prevents firing on the initial render, and filters
 * do not change during a search, so the effect only fires on a genuine refine
 * change. Selections are debounced so rapid multi-select coalesces into one
 * request instead of firing per change.
 */

import { useEffect, useRef } from 'react';
import type { SearchFilters } from './components/search-filter-types';

export function useFilterAutoSearch(
  filters: SearchFilters,
  hasSearched: boolean,
  runSearch: () => void,
  debounceMs = 400,
): void {
  // Keep the latest runSearch/hasSearched without making them effect deps, so
  // the effect fires only on a real `filters` change (not on every render).
  const runRef = useRef(runSearch);
  runRef.current = runSearch;
  const hasSearchedRef = useRef(hasSearched);
  hasSearchedRef.current = hasSearched;

  useEffect(() => {
    if (!hasSearchedRef.current) return;
    const timer = setTimeout(() => runRef.current(), debounceMs);
    return () => clearTimeout(timer);
  }, [filters, debounceMs]);
}
